// MIDAS V3 — <SrcRefV3> sources pill + the shared "info on hover" box
// (MIDAS-ANSWER-FORMAT-1 round 5, Paper file "MIDAS answer format — before /
// after", board S10, frame "Variant E" → "E sources" + the "INFO NA HOVER" box).
//
//   <src-ref kind="logic"/>              → [logic icon] Logika stroja
//   <src-ref kind="live" time="00:19"/>  → [pulse icon] Naživo zo stroja · 00:19 ●
//
// Values are taken from the Paper nodes (get_jsx / get_computed_styles), then
// mapped onto V3 tokens so all four palettes (p-claude / p-machineid / p-teal /
// p-slate) follow automatically:
//   pill   : padding 5×10, radius 16, gap 6, 1px border, 12/16 text
//            #FFFFFF → --card · #D9D5CB → --border · #1C1B19 → --ink
//   live   : border/text/icon/dot #2E9E5B → --green (the TRUE/✓ green the
//            LIVE pip already uses) · fill #EAF6EE → 10 % --green over --card
//   dot    : 6×6, radius 3, pulses (ring) — static under reduced motion
//   info   : #1C1B19 box → --ink, #FFFFFF text → --card, eyebrow #8C8983 →
//            --faint, radius 8, padding 12×14, gap 6, max width 420
// Inter (Paper) → var(--font-sans), JetBrains Mono → var(--font-mono): the V3
// type tokens. Classes + keyframes live in ./src-ref.css.
//
// Hover/focus surface = the SAME Radix tooltip primitive every V3 chip uses
// (../v2/ui/tooltip; the provider is mounted once in MidasV3Layout). Not
// clickable — the pill is a provenance statement, not a link (Paper notes §2:
// "info box, not a content snippet — that is later work").
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Tooltip, TooltipContent, TooltipTrigger } from "../v2/ui/tooltip";
import { prefersReducedMotion } from "../../utils/compRefFlash";
import type { SrcRefKind } from "./srcChipsGrammar";

// ─── Shared info box (Paper "INFO NA HOVER") ─────────────────────────

export interface SrcInfoBoxV3Props {
  /** Mono caps eyebrow — the chip's name (e.g. "Logika stroja"). */
  eyebrow: string;
  /** The info sentence. Rendered as a React text child — never markup. */
  body: string;
  /** Box cap in px (Paper: 420). r6 <cond-info/> passes 320 (SEC-REQ-CI-4). */
  maxWidth?: number;
  /** Let a long unbroken token wrap anywhere (r6 <cond-info/> free text). */
  wrapAnywhere?: boolean;
}

/** The box's inner markup (eyebrow + sentence), shared by the hover tooltip
 *  below and the r6 <cond-info/> tap popover (CondInfoV3). */
export const SrcInfoBoxBodyV3: React.FC<Omit<SrcInfoBoxV3Props, "maxWidth">> = ({
  eyebrow,
  body,
  wrapAnywhere = false,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        lineHeight: "12px",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--faint)",
      }}
    >
      {eyebrow}
    </div>
    <div
      style={{
        fontSize: 13,
        lineHeight: "18px",
        ...(wrapAnywhere ? { overflowWrap: "anywhere" as const } : {}),
      }}
    >
      {body}
    </div>
  </div>
);

export const SrcInfoBoxV3: React.FC<SrcInfoBoxV3Props> = ({
  eyebrow,
  body,
  maxWidth = 420,
  wrapAnywhere = false,
}) => (
  <TooltipContent
    side="top"
    style={{
      // Explicit surface — the shadcn `bg-popover` token is undefined on
      // .midas-v3-root (see LiveRefV3.tsx). Paper draws this box dark: the
      // V3 ink token, with the card token as its text colour.
      background: "var(--ink)",
      color: "var(--card)",
      border: "none",
      boxShadow: "var(--shadow)",
      borderRadius: 8,
      padding: "12px 14px",
      maxWidth,
      fontFamily: "var(--font-sans)",
    }}
  >
    <SrcInfoBoxBodyV3 eyebrow={eyebrow} body={body} wrapAnywhere={wrapAnywhere} />
  </TooltipContent>
);

// ─── Icons — exact Paper geometry, stroke follows the pill colour ────

const LogicIcon: React.FC = () => (
  <svg
    className="src-ref__icon"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="2" y="2" width="10" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 7h2l1-2 1 4 1-2h1.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const PulseIcon: React.FC = () => (
  <svg
    className="src-ref__icon"
    width="18"
    height="14"
    viewBox="0 0 18 14"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M1 7h3l2-5 3 10 3-7 1.5 2H17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

// ─── The pill ─────────────────────────────────────────────────────────

export interface SrcRefV3Props {
  kind: SrcRefKind;
  /** `HH:MM` — required for kind "live" (validated upstream). */
  time?: string;
}

export const SrcRefV3: React.FC<SrcRefV3Props> = ({ kind, time }) => {
  const { translate } = useTranslation("chatBot");
  const isLive = kind === "live";
  // The parser never builds a live pill without a time; stay fail-closed if a
  // caller ever does — a "live" claim with no clock is not shown.
  if (isLive && !time) return null;

  const label = isLive
    ? (translate("srcRef.live.label", {
        defaultValue: "Live from the machine · {{time}}",
        time,
      }) as string)
    : (translate("srcRef.logic.label", { defaultValue: "Machine logic" }) as string);
  const eyebrow = isLive
    ? (translate("srcRef.live.eyebrow", { defaultValue: "Live from the machine" }) as string)
    : label;
  const body = isLive
    ? (translate("srcRef.live.tooltip", {
        defaultValue: "Live values read from the machine at {{time}}",
        time,
      }) as string)
    : (translate("srcRef.logic.tooltip", {
        defaultValue: "This answer used the machine's control logic (PLC program)",
      }) as string);

  // Reduced motion is decided in JS as well as in CSS (src-ref.css media
  // query) so the static fallback is deterministic and testable.
  const className = [
    "src-ref",
    isLive ? "src-ref--live" : "src-ref--logic",
    isLive && prefersReducedMotion() ? "src-ref--static" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className} tabIndex={0} data-src-ref={kind}>
          {isLive ? <PulseIcon /> : <LogicIcon />}
          <span className="src-ref__label">{label}</span>
          {isLive && <span className="src-ref__dot" aria-hidden="true" />}
        </span>
      </TooltipTrigger>
      <SrcInfoBoxV3 eyebrow={eyebrow} body={body} />
    </Tooltip>
  );
};

export default SrcRefV3;
